import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import type { PlanItemDTO } from '@clear/contracts';
import {
  clearable,
  editLock,
  floorQueues,
  maxClearable,
  needsAction,
  pendingSince,
  type Gate,
} from '@clear/domain';
import { formatNumber } from '@clear/i18n';
import { Button, Chip, ConfirmButton, Empty, Icon, Spinner } from '@clear/ui';
import { useGateActions } from '../components/actions';
import { gateLabelFor, ItemDots, ModelMeta, Thumb } from '../components/item';
import { ItemSheetHost, PageActions, PageHead, Shell, useItemParam } from '../components/Shell';
import { PartialSheet, ReasonSheet } from '../components/sheets';
import { useLookups, useSelectedLine, type Lookups } from '../data/bootstrap';
import { NO_ITEMS, usePlan } from '../data/plan';
import { useI18n } from '../i18n';
import { age, fmtDate, hhmm } from '../lib/time';

type Tab = 'todo' | 'issues' | 'done';
type SheetState = { kind: 'part' | 'bad' | 'obs'; item: PlanItemDTO } | null;

function JobCard({
  item,
  gate,
  lk,
  mode,
  focused,
  editing,
  onEdit,
  onSheet,
}: {
  item: PlanItemDTO;
  gate: Gate;
  lk: Lookups;
  mode: Tab;
  focused?: boolean;
  editing?: boolean;
  onEdit?: (on: boolean) => void;
  onSheet: (s: SheetState) => void;
}) {
  const { t } = useI18n();
  const actions = useGateActions(gate);
  const [, openItem] = useItemParam();
  const ref = useRef<HTMLElement>(null);
  const m = lk.model(item.modelId);
  const e = item.gates[gate];
  const q = gate === 'quality' ? clearable(item) : item.qty;
  const added = e && e.status === 'ok' && needsAction(item, gate) ? maxClearable(item, gate) - e.okQty : 0;

  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [focused]);

  const buttons = (
    <div className="acts">
      <button type="button" className="act act--ok" onClick={() => actions.ok(item)}>
        <Icon name="check" size={20} />
        {t('gateStatus.ok')}
        <kbd>O</kbd>
      </button>
      <button type="button" className="act act--part" onClick={() => onSheet({ kind: 'part', item })}>
        {t(`gateStatus.partial.${gate}`)}
        <kbd>P</kbd>
      </button>
      <button type="button" className="act act--bad" onClick={() => onSheet({ kind: 'bad', item })}>
        {t(`gateStatus.blocked.${gate}`)}
        <kbd>S</kbd>
      </button>
    </div>
  );

  let tail: React.ReactNode = buttons;
  if (mode !== 'todo' && !editing && e) {
    const lock = editLock(item, gate);
    const tone = e.status === 'ok' ? 'ok' : e.status === 'partial' && gate !== 'quality' ? 'wait' : 'hold';
    let lockText = '';
    let phone: string | null | undefined;
    if (lock === 'quality_acted') {
      const who = lk.user(item.gates.quality?.userId);
      lockText = t('floor.lockedQuality', { name: who?.name ?? '—', ext: who?.ext ?? '—' });
      phone = who?.mobile;
    } else if (lock === 'started') {
      const who = lk.user(item.startedBy);
      lockText = t('floor.lockedStarted', { name: who?.name ?? '—', ext: who?.ext ?? '—' });
      phone = who?.mobile;
    }
    tail = (
      <div className="card__status">
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip tone={tone}>
            {gateLabelFor(t, gate, e.status)}
            {e.status === 'partial' ? ` ${formatNumber(e.okQty)}` : ''}
          </Chip>
          <span className="txt">
            {e.reasonId ? `${lk.reasonLabel(e.reasonId)} · ` : ''}
            {hhmm(e.at, lk.tz)} · {lk.userName(e.userId)}
          </span>
        </span>
        {lock ? (
          <span className="lockline">
            <Icon name="lock" />
            {phone ? <a href={`tel:${phone}`}>{lockText}</a> : lockText}
          </span>
        ) : (
          <span style={{ display: 'flex', gap: 8 }}>
            {gate === 'quality' && e.status === 'ok' && !e.reasonId && (
              <Button size="sm" variant="ghost" onClick={() => onSheet({ kind: 'obs', item })}>
                {t('gateStatus.observation')}
              </Button>
            )}
            <Button size="sm" onClick={() => onEdit?.(true)}>
              {t('common.change')}
            </Button>
          </span>
        )}
      </div>
    );
  } else if (editing) {
    tail = (
      <>
        {buttons}
        <Button variant="link" onClick={() => onEdit?.(false)}>
          {t('floor.cancelEdit')}
        </Button>
      </>
    );
  }

  return (
    <article ref={ref} className={`card${focused ? ' card--focus' : ''}`} aria-current={focused || undefined}>
      <div className="card__head">
        <button
          type="button"
          onClick={() => openItem(item.id)}
          aria-label={`${m.name} ${m.finish}`}
          style={{ borderRadius: 13 }}
        >
          <Thumb modelId={item.modelId} lk={lk} />
        </button>
        <div>
          <div className="card__name">{m.name}</div>
          <div className="card__meta">
            <ModelMeta modelId={item.modelId} lk={lk} />
          </div>
        </div>
        <div className="card__qty">
          <b>{formatNumber(q)}</b>
          <span>
            {gate === 'quality'
              ? q < item.qty
                ? t('floor.clearableOf', { n: formatNumber(item.qty) })
                : t('floor.toInspect')
              : t('common.units')}
          </span>
        </div>
      </div>
      {(item.priority === 'high' || added > 0) && (
        <div className="card__tags">
          {item.priority === 'high' && <span className="pri pri--high">{t('priority.highPriority')}</span>}
          {added > 0 && (
            <Chip tone="wait" icon="plus">
              {gate === 'quality'
                ? t('floor.moreArrived', { n: formatNumber(added) })
                : t('floor.plusAdded', { n: formatNumber(added) })}
            </Chip>
          )}
        </div>
      )}
      {tail}
    </article>
  );
}

export default function FloorPage({ gate }: { gate: Gate }) {
  const { t, lang } = useI18n();
  const { tab = 'todo' } = useParams();
  const lk = useLookups();
  const [line] = useSelectedLine(lk);
  const plan = usePlan({ lineId: line?.id });
  const actions = useGateActions(gate);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [focus, setFocus] = useState(0);
  const [itemParam] = useItemParam();

  const items = plan.data?.items ?? NO_ITEMS;
  const Q = useMemo(() => floorQueues(items, gate), [items, gate]);
  const todo = Q.todo;
  const focusIdx = Math.min(focus, Math.max(0, todo.length - 1));

  // Laptop keyboard (PRD): ↑ ↓ move between cards, O = OK, P = Partial, S = Short/Hold, A = All OK.
  const allOkRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (tab !== 'todo' || sheet || itemParam) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const cur = todo[focusIdx];
      const k = e.key.toLowerCase();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocus((f) => Math.min(f + 1, todo.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocus((f) => Math.max(f - 1, 0));
      } else if (k === 'o' && cur) actions.ok(cur);
      else if (k === 'p' && cur) setSheet({ kind: 'part', item: cur });
      else if (k === 's' && cur) setSheet({ kind: 'bad', item: cur });
      else if (k === 'a' && todo.length > 1) allOkRef.current?.click();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tab, sheet, itemParam, todo, focusIdx, actions]);

  if (!['todo', 'issues', 'done'].includes(tab)) return <Navigate to={`/${gate}`} replace />;
  if (!lk || !plan.data)
    return (
      <Shell title={t('floor.myWork', { gate: t(`gates.${gate}`) })}>
        {<Spinner label={t('common.loading')} />}
      </Shell>
    );

  const oldest = todo.length ? Math.min(...todo.map((i) => pendingSince(i, gate))) : null;
  const snap = plan.data;
  const published = snap.plan?.status === 'published';
  const title = t('floor.myWork', { gate: t(`gates.${gate}`) });
  const bad = t(`gateStatus.blocked.${gate}`);

  let body: React.ReactNode;
  if (!published) {
    body = (
      <Empty title={snap.plan ? t('floor.planNotPublished') : t('floor.noPlan')}>
        {t('floor.planNotPublishedHint')}
      </Empty>
    );
  } else if (tab === 'todo') {
    body = (
      <>
        {todo.length > 0 && oldest != null && (
          <div className="bulk">
            <p className="muted">
              {t('floor.pendingSince', { time: hhmm(oldest, lk.tz), age: age(oldest) })}
              <span className="kbd-hint">{t('floor.keyboardHint')}</span>
            </p>
          </div>
        )}
        {todo.length ? (
          <div className="cards cards--2">
            {todo.map((i, idx) => (
              <JobCard
                key={i.id}
                item={i}
                gate={gate}
                lk={lk}
                mode="todo"
                focused={idx === focusIdx}
                onSheet={setSheet}
              />
            ))}
          </div>
        ) : (
          <Empty title={t('floor.allClear')}>{t('floor.noTodo')}</Empty>
        )}
        {Q.waiting.length > 0 && (
          <section className="stack">
            <p className="eyebrow">
              {t('floor.waitingFor')} · {Q.waiting.length}
            </p>
            <div className="list">
              {Q.waiting.map((i) => {
                const m = lk.model(i.modelId);
                return (
                  <div className="row" key={i.id}>
                    <Thumb modelId={i.modelId} lk={lk} size={36} />
                    <span>
                      <span className="row__title">{m.name}</span>
                      <span className="row__sub">{m.finish}</span>
                    </span>
                    <span className="row__end">
                      <span className="num">{formatNumber(i.qty)}</span>
                      <ItemDots item={i} lk={lk} small />
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {todo.length > 1 && (
          <PageActions>
            <ConfirmButton
              size="lg"
              variant="line"
              className="dock__btn btn--okline"
              icon="check"
              label={t('floor.allOk', { n: todo.length })}
              confirmLabel={t('floor.confirmAllOk', { n: todo.length })}
              onConfirm={() => actions.allOk(todo)}
              ref={allOkRef}
            />
          </PageActions>
        )}
      </>
    );
  } else {
    const arr = tab === 'issues' ? Q.issues : Q.done;
    body = arr.length ? (
      <div className="cards cards--2">
        {arr.map((i) => (
          <JobCard
            key={i.id}
            item={i}
            gate={gate}
            lk={lk}
            mode={tab as Tab}
            editing={editing === i.id}
            onEdit={(on) => setEditing(on ? i.id : null)}
            onSheet={(s) => {
              setEditing(null);
              setSheet(s);
            }}
          />
        ))}
      </div>
    ) : tab === 'issues' ? (
      <Empty title={t('floor.noIssues')}>{t('floor.noIssuesHint', { bad })}</Empty>
    ) : (
      <Empty title={t('floor.noDone')}>{t('floor.noDoneHint')}</Empty>
    );
  }

  return (
    <Shell title={title} waiting={todo.length}>
      <PageHead
        title={title}
        sub={`${line?.name ?? ''} · ${lk.shift(snap.shiftId) ? t('common.shift', { code: lk.shift(snap.shiftId)!.code }) : ''} · ${fmtDate(snap.date, lang)}`}
      />
      {body}
      {sheet?.kind === 'part' && (
        <PartialSheet item={sheet.item} gate={gate} lk={lk} onClose={() => setSheet(null)} />
      )}
      {sheet?.kind === 'bad' && (
        <ReasonSheet item={sheet.item} gate={gate} lk={lk} onClose={() => setSheet(null)} />
      )}
      {sheet?.kind === 'obs' && (
        <ReasonSheet
          item={sheet.item}
          gate={gate}
          lk={lk}
          mode="observation"
          onClose={() => setSheet(null)}
        />
      )}
      <ItemSheetHost items={items} lk={lk} />
    </Shell>
  );
}
